import React, { useContext, useEffect, useState } from 'react'
import { GlobalContext } from '../../../../App'
import { EPCRContext } from '../EPCR'
import {
  MOBILE_HELPER_TEXT,
  MOBILE_LOCALES,
  regionLabels,
} from '../../../../helpers/region'
import AnDateField from '../../../../components/DateTime/DateField'
import SimpleTextField from '../../../../components/Forms/SimpleTextField'
import moment from 'moment'
//@MUI
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import NextButton from '../NextButton'
import ParticipantLookup from './ParticipantLookup'
import { TextField } from '@mui/material'
import validator from 'validator'

const DemographicsModule = () => {
  const { formValues, handleChange, setDownloadState } = useContext(EPCRContext)
  const { tenantInfo } = useContext(GlobalContext)
  const labels = regionLabels(tenantInfo?.region)
  const [preferredNameEdited, setPreferredNameEdited] = useState(false)
  const [feedbackPhoneNumberEdited, setFeedbackPhoneNumberEdited] =
    useState(false)
  const [numberForFeedback, setNumberForFeedback] = useState(
    formValues?.demographics?.numberForFeedback ?? '',
  )

  useEffect(() => {
    if (formValues?.demographics?.preferredName) setPreferredNameEdited(true)
    if (formValues?.demographics?.numberForFeedback)
      setFeedbackPhoneNumberEdited(true)
  }, [])

  const phoneNumberValid = validator.isMobilePhone(
    numberForFeedback,
    MOBILE_LOCALES,
  )

  return (
    <Stack gap={1}>
      <ParticipantLookup />
      <EPCRCard>
        <Typography variant="title2">Demographics</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <SimpleTextField
              value={formValues?.demographics?.firstName ?? ''}
              label="First Name"
              onChange={(e) => {
                handleChange({
                  moduleName: 'demographics',
                  field: 'firstName',
                  value: e.target.value,
                })
                if (!preferredNameEdited) {
                  handleChange({
                    moduleName: 'demographics',
                    field: 'preferredName',
                    value: e.target.value,
                  })
                }
              }}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <SimpleTextField
              value={formValues?.demographics?.lastName}
              label="Last Name"
              onChange={(e) => {
                handleChange({
                  moduleName: 'demographics',
                  field: 'lastName',
                  value: e.target.value,
                })
                handleChange({
                  moduleName: 'demographics',
                  field: 'lastNameUnknown',
                  value: false,
                })
              }}
              InputProps={{
                endAdornment: (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formValues?.demographics?.lastNameUnknown}
                        onChange={(e) => {
                          handleChange({
                            moduleName: 'demographics',
                            field: 'lastNameUnknown',
                            value: !!e.target.checked,
                          })
                          handleChange({
                            moduleName: 'demographics',
                            field: 'lastName',
                            value: '',
                          })
                        }}
                      />
                    }
                    label="Unknown"
                  />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <SimpleTextField
              value={formValues?.demographics?.preferredName ?? ''}
              label="Preferred Name"
              onChange={(e) => {
                handleChange({
                  moduleName: 'demographics',
                  field: 'preferredName',
                  value: e.target.value,
                })
                !preferredNameEdited && setPreferredNameEdited(true)
              }}
            />
          </Grid>
          <Grid item xs={12} lg={6} display="flex" gap={1}>
            <AnDateField
              fullWidth
              disableFuture
              label="Date of Birth"
              required={!formValues?.demographics?.dobUnknown}
              onChange={(newDate) => {
                handleChange({
                  moduleName: 'demographics',
                  field: 'dob',
                  value: newDate,
                })
                handleChange({
                  moduleName: 'demographics',
                  field: 'dobUnknown',
                  value: false,
                })
                // The "Under 18" flag only applies while the DOB is unknown
                handleChange({
                  moduleName: 'demographics',
                  field: 'dobUnknownButPaediatric',
                  value: false,
                })
              }}
              value={
                formValues?.demographics?.dob
                  ? moment(formValues?.demographics?.dob)
                  : null
              }
              InputProps={{
                endAdornment: (
                  <Stack direction={{ xs: 'column', lg: 'row' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!formValues?.demographics?.dobUnknown}
                          onChange={(e) => {
                            handleChange({
                              moduleName: 'demographics',
                              field: 'dobUnknown',
                              value: !!e.target.checked,
                            })
                            handleChange({
                              moduleName: 'demographics',
                              field: 'dob',
                              value: null,
                            })
                            if (!e.target.checked) {
                              handleChange({
                                moduleName: 'demographics',
                                field: 'dobUnknownButPaediatric',
                                value: false,
                              })
                            }
                          }}
                        />
                      }
                      label="Unknown"
                    />
                    {formValues?.demographics?.dobUnknown && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              !!formValues?.demographics
                                ?.dobUnknownButPaediatric
                            }
                            onChange={(e) => {
                              handleChange({
                                moduleName: 'demographics',
                                field: 'dobUnknownButPaediatric',
                                value: !!e.target.checked,
                              })
                            }}
                          />
                        }
                        label="Under 18"
                      />
                    )}
                  </Stack>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={formValues?.demographics?.gender ?? ''}
                label="Gender"
                onChange={(e) =>
                  handleChange({
                    moduleName: 'demographics',
                    field: 'gender',
                    value: e.target.value,
                  })
                }
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Non-binary">Non-Binary</MenuItem>
                <MenuItem value="Unknown">Unknown</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} lg={6}>
            <SimpleTextField
              value={formValues?.demographics?.contactNumber}
              label="Contact Number"
              onChange={(e) => {
                handleChange({
                  moduleName: 'demographics',
                  field: 'contactNumber',
                  value: e.target.value,
                })
                if (!feedbackPhoneNumberEdited) {
                  handleChange({
                    moduleName: 'demographics',
                    field: 'numberForFeedback',
                    value: e.target.value,
                  })
                  setNumberForFeedback(e.target.value)
                }
              }}
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <SimpleTextField
              value={formValues?.demographics?.gpSurgery}
              label="GP Surgery"
              onChange={(e) =>
                handleChange({
                  moduleName: 'demographics',
                  field: 'gpSurgery',
                  value: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <SimpleTextField
              value={formValues?.demographics?.nhsNumber}
              onChange={(e) =>
                handleChange({
                  moduleName: 'demographics',
                  field: 'nhsNumber',
                  value: e.target.value,
                })
              }
              label={labels.nationalIdLabel}
            />
          </Grid>
        </Grid>
      </EPCRCard>
      <EPCRCard>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="title3">Next of Kin</Typography>
          </Grid>
          <Grid item xs={12} lg={8}>
            <SimpleTextField
              label="Name"
              value={formValues?.demographics?.nokName}
              onChange={(e) =>
                handleChange({
                  moduleName: 'demographics',
                  field: 'nokName',
                  value: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <SimpleTextField
              label="Relationship"
              value={formValues?.demographics?.nokRelationship}
              onChange={(e) =>
                handleChange({
                  moduleName: 'demographics',
                  field: 'nokRelationship',
                  value: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <SimpleTextField
              label="Contact Number"
              value={formValues?.demographics?.nokContactNumber}
              onChange={(e) =>
                handleChange({
                  moduleName: 'demographics',
                  field: 'nokContactNumber',
                  value: e.target.value,
                })
              }
            />
          </Grid>
        </Grid>
      </EPCRCard>
      <EPCRCard>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="title3">Address</Typography>
          </Grid>
          <Grid item xs={12} lg={6}>
            <SimpleTextField
              label="Address Line 1"
              value={formValues?.demographics?.addressLine1}
              onChange={(e) =>
                handleChange({
                  moduleName: 'demographics',
                  field: 'addressLine1',
                  value: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} lg={6}>
            <SimpleTextField
              label="Address Line 2"
              value={formValues?.demographics?.addressLine2}
              onChange={(e) =>
                handleChange({
                  moduleName: 'demographics',
                  field: 'addressLine2',
                  value: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <SimpleTextField
              label="Town"
              value={formValues?.demographics?.town}
              onChange={(e) =>
                handleChange({
                  moduleName: 'demographics',
                  field: 'town',
                  value: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <SimpleTextField
              label="City"
              value={formValues?.demographics?.city}
              onChange={(e) =>
                handleChange({
                  moduleName: 'demographics',
                  field: 'city',
                  value: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <SimpleTextField
              label="Post Code"
              value={formValues?.demographics?.postCode}
              onChange={(e) =>
                handleChange({
                  moduleName: 'demographics',
                  field: 'postCode',
                  value: e.target.value,
                })
              }
            />
          </Grid>
        </Grid>
      </EPCRCard>
      <EPCRCard>
        <Typography variant="title3">Consent for Feedback</Typography>
        <Typography variant="caption">
          Patient feedback is very important. If the patient consents, enter
          their phone number below to send them a link to the feedback page.
        </Typography>
        <Stack direction={{ xs: 'column', lg: 'row' }} gap={1}>
          <Box flex={1}>
            <FormControl fullWidth>
              <InputLabel>Consent for Feedback</InputLabel>
              <Select
                value={formValues?.demographics?.consentsToSMS ?? true}
                onChange={(e) =>
                  handleChange({
                    moduleName: 'demographics',
                    field: 'consentsToSMS',
                    value: e.target.value,
                  })
                }
                label="Consent for Feedback"
              >
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box flex={1}>
            <TextField
              fullWidth
              onChange={(e) => {
                handleChange({
                  moduleName: 'demographics',
                  field: 'numberForFeedback',
                  value: e.target.value,
                })
                setNumberForFeedback(e.target.value)
                !feedbackPhoneNumberEdited && setFeedbackPhoneNumberEdited(true)
              }}
              value={formValues?.demographics?.numberForFeedback ?? ''}
              disabled={formValues?.review?.consentsToSMS === false}
              label="Phone Number"
              helperText={!phoneNumberValid && MOBILE_HELPER_TEXT}
              error={
                !!formValues?.demographics?.numberForFeedback &&
                !phoneNumberValid
              }
            />
          </Box>
        </Stack>
      </EPCRCard>
      <NextButton newModule="PMHx" />
    </Stack>
  )
}

export default DemographicsModule
